import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import Breadcrumb from "@/components/base/Breadcrumb";
import { chapters, filmInfo, timelineSegments } from "@/data/chapters";
import { useFavorites } from "@/hooks/useFavorites";
import { usePageMeta } from "@/hooks/usePageMeta";

export default function ChapterDetail() {
  const { id } = useParams<{ id: string }>();
  const chapter = chapters.find((item) => item.id === id);

  usePageMeta({
    title: chapter
      ? `色戒无删减版 - 第${chapter.index}章 ${chapter.title}剧情与在线播放`
      : "章节不存在 - 色戒无删减版",
    description: chapter
      ? `${chapter.excerpt} 本章约 ${chapter.duration} 分钟，属于剧情${chapter.segment}阶段，附详细剧情解说与播放区域演示。`
      : "您访问的章节不存在或已被调整，请返回章节分类页重新选择。",
    keywords: chapter
      ? `色戒第${chapter.index}章,${chapter.title},色戒剧情介绍,色戒无删减版在线观看`
      : "色戒无删减版,色戒章节",
  });

  const { isFavorite, toggleFavorite } = useFavorites();
  const [noticeOpen, setNoticeOpen] = useState(false);
  const [shareTip, setShareTip] = useState("");

  const related = useMemo(() => {
    if (!chapter) return [];
    return chapters
      .filter((item) => item.id !== chapter.id)
      .map((item) => ({
        item,
        score:
          item.tags.filter((tag) => chapter.tags.includes(tag)).length * 2 +
          (item.segment === chapter.segment ? 1 : 0),
      }))
      .sort((a, b) => b.score - a.score || a.item.index - b.item.index)
      .slice(0, 4)
      .map((entry) => entry.item);
  }, [chapter]);

  if (!chapter) {
    return (
      <div className="min-h-screen bg-background-50">
        <SiteHeader />
        <main className="container-page flex min-h-[70vh] flex-col items-center justify-center py-24 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full border border-accent-500/30 bg-accent-500/10 text-accent-400">
            <i className="ri-film-line text-3xl leading-none" />
          </span>
          <h1 className="mt-6 font-heading text-2xl text-foreground-950">没有找到这个章节</h1>
          <p className="mt-3 max-w-md text-sm text-foreground-600">
            你访问的章节可能已经调整编号。可以回到章节分类页，按时间线重新选择想看的段落。
          </p>
          <div className="mt-7 flex flex-col gap-3 sm:flex-row">
            <Link
              to="/category"
              className="whitespace-nowrap rounded-md bg-accent-500 px-6 py-3 font-label text-sm font-medium text-background-50"
            >
              浏览全部章节
            </Link>
            <Link
              to="/"
              className="whitespace-nowrap rounded-md border border-background-300 px-6 py-3 font-label text-sm text-foreground-800"
            >
              返回首页
            </Link>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const favorite = isFavorite(chapter.id);
  const prev = chapters.find((item) => item.index === chapter.index - 1);
  const next = chapters.find((item) => item.index === chapter.index + 1);

  const handleShare = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      setShareTip("章节链接已复制，可以分享给朋友了");
    } catch {
      setShareTip(`复制失败，可手动复制地址：${url}`);
    }
    window.setTimeout(() => setShareTip(""), 3200);
  };

  return (
    <div className="min-h-screen bg-background-50">
      <SiteHeader />

      <main>
        <div className="border-b border-background-200 pt-24 md:pt-32">
          <div className="container-page pb-8">
            <Breadcrumb
              items={[
                { label: "首页", to: "/" },
                { label: "章节分类", to: "/category" },
                { label: `第 ${chapter.index} 章 ${chapter.title}` },
              ]}
            />

            <div className="mt-5 flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded-md bg-primary-500 px-2.5 py-1 font-label text-[11px] text-foreground-950">
                    {chapter.segment}
                  </span>
                  <span className="font-label text-[11px] tracking-[0.2em] text-accent-500">
                    第 {String(chapter.index).padStart(2, "0")} 章
                  </span>
                  <span className="font-label text-[11px] text-foreground-500">· {chapter.timeline}</span>
                </div>
                <h1 className="mt-3 font-heading text-2xl leading-snug text-foreground-950 md:text-[38px]">
                  {chapter.title}
                </h1>
                <p className="mt-2 font-label text-xs text-foreground-500">
                  色戒无删减版 · 完整剧情档案 · 本章约 {chapter.duration} 分钟
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => toggleFavorite(chapter.id)}
                  className={`inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border px-4 py-2.5 font-label text-sm transition-colors ${
                    favorite
                      ? "border-accent-500 bg-accent-500/10 text-accent-400"
                      : "border-background-300 text-foreground-800 hover:border-accent-500/60"
                  }`}
                >
                  <i className={`${favorite ? "ri-bookmark-3-fill" : "ri-bookmark-3-line"} text-base leading-none`} />
                  {favorite ? "已收藏本章" : "收藏本章"}
                </button>
                <button
                  type="button"
                  onClick={handleShare}
                  className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-4 py-2.5 font-label text-sm text-foreground-800 transition-colors hover:border-accent-500/60 hover:text-accent-400"
                >
                  <i className="ri-share-forward-line text-base leading-none" />
                  分享
                </button>
              </div>
            </div>
            {shareTip && (
              <p className="mt-3 inline-flex items-center gap-1.5 rounded-md border border-accent-500/30 bg-accent-500/10 px-3 py-1.5 text-xs text-accent-400">
                <i className="ri-check-line text-sm leading-none" />
                {shareTip}
              </p>
            )}
          </div>
        </div>

        <div className="container-page py-8 md:py-12">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1fr_320px] lg:gap-10">
            <div className="min-w-0">
              <div className="relative aspect-video w-full overflow-hidden rounded-lg border border-background-200 bg-background-100">
                <img
                  src={chapter.cover}
                  alt={`${chapter.title} 播放区域示意背景`}
                  className="h-full w-full object-cover object-top opacity-35"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/50 to-background-50/20" />
                <span className="absolute left-4 top-4 inline-flex items-center gap-1.5 rounded-full bg-background-50/75 px-3 py-1 font-label text-[11px] text-accent-400 backdrop-blur">
                  <i className="ri-error-warning-line text-xs leading-none" />
                  前端演示 · 仅示意播放区域
                </span>
                <div className="absolute inset-0 flex flex-col items-center justify-center px-6 text-center">
                  <button
                    type="button"
                    onClick={() => setNoticeOpen((open) => !open)}
                    aria-label="播放演示说明"
                    className="group flex h-16 w-16 cursor-pointer items-center justify-center rounded-full bg-accent-500 text-background-50 transition-transform hover:scale-105 md:h-20 md:w-20"
                  >
                    <i className="ri-play-fill text-3xl leading-none md:text-4xl" />
                  </button>
                  <p className="mt-4 max-w-lg font-heading text-sm text-foreground-950 md:text-lg">
                    待接入正式播放接口
                  </p>
                  <p className="mt-1.5 hidden max-w-md text-xs leading-relaxed text-foreground-600 sm:block">
                    当前为前端演示，仅示意播放区域，不含真实片源。点击播放按钮可查看说明。
                  </p>
                </div>
                <span className="absolute bottom-4 right-4 hidden rounded-md bg-background-50/75 px-2.5 py-1 font-label text-[11px] text-foreground-800 backdrop-blur sm:block">
                  {chapter.timeline} · {chapter.duration} 分钟
                </span>
              </div>

              {noticeOpen && (
                <div className="mt-3 flex flex-col gap-3 rounded-lg border border-accent-500/30 bg-accent-500/[0.07] p-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-start gap-3">
                    <i className="ri-information-line mt-0.5 text-lg leading-none text-accent-400" />
                    <p className="text-xs leading-relaxed text-foreground-700">
                      本站播放器为界面演示模块，未接入任何播放源或第三方接口。若后续需要上线，可在后台配置播放地址后替换此区域。我们也建议通过正规渠道观看正版影片。
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setNoticeOpen(false)}
                    className="shrink-0 cursor-pointer self-end whitespace-nowrap rounded-md border border-background-300 px-3 py-1.5 font-label text-[11px] text-foreground-700 sm:self-auto"
                  >
                    知道了
                  </button>
                </div>
              )}

              <div className="mt-4 flex flex-wrap items-center gap-3 rounded-md border border-background-200 bg-background-100 px-4 py-3 font-label text-[11px] text-foreground-500">
                <span className="flex items-center gap-1.5">
                  <i className="ri-movie-2-line text-sm leading-none text-accent-500" />
                  播放源：演示模块（未接入）
                </span>
                <span className="hidden h-3 w-px bg-background-300 sm:block" />
                <span className="flex items-center gap-1.5">
                  <i className="ri-hd-line text-sm leading-none text-accent-500" />
                  清晰度：示意
                </span>
                <span className="hidden h-3 w-px bg-background-300 sm:block" />
                <span className="flex items-center gap-1.5">
                  <i className="ri-closed-captioning-line text-sm leading-none text-accent-500" />
                  字幕：示意
                </span>
              </div>

              <article className="mt-10">
                <div className="flex items-center gap-2">
                  <span className="h-px w-8 bg-accent-500" />
                  <h2 className="font-label text-xs tracking-[0.28em] text-accent-500">本章剧情</h2>
                </div>
                <h3 className="mt-3 font-heading text-xl text-foreground-950 md:text-2xl">{chapter.excerpt}</h3>
                <div className="mt-5 space-y-4">
                  {chapter.paragraphs.map((paragraph, index) => (
                    <p key={index} className="text-sm leading-[1.95] text-foreground-600 md:text-[15px]">
                      {paragraph}
                    </p>
                  ))}
                </div>

                <div className="mt-6 flex flex-wrap gap-2">
                  {chapter.tags.map((tag) => (
                    <span
                      key={tag}
                      className="rounded-md bg-secondary-100 px-2.5 py-1 font-label text-[11px] text-secondary-900"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </article>

              <section className="surface-card mt-10 p-5 md:p-6">
                <div className="flex items-center justify-between">
                  <h2 className="font-heading text-base text-foreground-950">影片基本信息</h2>
                  <span className="font-label text-[11px] text-foreground-500">资料仅作示例</span>
                </div>
                <dl className="mt-5 grid grid-cols-1 gap-x-8 gap-y-4 sm:grid-cols-2">
                  {[
                    { label: "影片名称", value: filmInfo.title },
                    { label: "上映年份", value: filmInfo.year },
                    { label: "导演", value: filmInfo.director },
                    { label: "主演", value: filmInfo.stars },
                    { label: "影片时长", value: filmInfo.runtime },
                    { label: "类型 / 地区", value: `${filmInfo.genre} · ${filmInfo.region}` },
                  ].map((row) => (
                    <div key={row.label} className="flex flex-col gap-1 border-b border-background-200/70 pb-3">
                      <dt className="font-label text-[11px] tracking-[0.16em] text-foreground-500">{row.label}</dt>
                      <dd className="text-sm text-foreground-900">{row.value}</dd>
                    </div>
                  ))}
                </dl>
              </section>

              <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                {prev ? (
                  <Link
                    to={`/detail/${prev.id}`}
                    className="group flex items-center gap-3 rounded-md border border-background-300 px-4 py-3 transition-colors hover:border-accent-500/60"
                  >
                    <i className="ri-arrow-left-line text-base leading-none text-foreground-500 group-hover:text-accent-400" />
                    <span className="flex flex-col">
                      <span className="font-label text-[11px] text-foreground-500">上一章</span>
                      <span className="text-sm text-foreground-900">{prev.title}</span>
                    </span>
                  </Link>
                ) : (
                  <span className="hidden sm:block" />
                )}
                {next && (
                  <Link
                    to={`/detail/${next.id}`}
                    className="group flex items-center justify-end gap-3 rounded-md border border-background-300 px-4 py-3 text-right transition-colors hover:border-accent-500/60 sm:w-auto"
                  >
                    <span className="flex flex-col">
                      <span className="font-label text-[11px] text-foreground-500">下一章</span>
                      <span className="text-sm text-foreground-900">{next.title}</span>
                    </span>
                    <i className="ri-arrow-right-line text-base leading-none text-foreground-500 group-hover:text-accent-400" />
                  </Link>
                )}
              </div>
            </div>

            <aside className="flex flex-col gap-5">
              <div className="surface-card p-5">
                <h2 className="font-heading text-sm text-foreground-950">所属分类</h2>
                <p className="mt-2 text-xs leading-relaxed text-foreground-600">
                  本章属于 <span className="text-accent-400">{chapter.segment}</span> 阶段，编号 {chapter.index} /{" "}
                  {chapters.length}。
                </p>
                <div className="mt-4 flex flex-wrap gap-1.5">
                  {timelineSegments.map((segment) => (
                    <Link
                      key={segment}
                      to="/category"
                      className={`rounded-full border px-2.5 py-1 font-label text-[11px] transition-colors ${
                        segment === chapter.segment
                          ? "border-accent-500/60 bg-accent-500/10 text-accent-400"
                          : "border-background-300 text-foreground-600 hover:border-accent-500/40"
                      }`}
                    >
                      {segment}
                    </Link>
                  ))}
                </div>
                <Link
                  to="/category"
                  className="mt-4 inline-flex items-center gap-1 font-label text-xs text-accent-500 hover:text-accent-400"
                >
                  查看全部章节
                  <i className="ri-arrow-right-line text-sm leading-none" />
                </Link>
              </div>

              <div className="surface-card p-5">
                <h2 className="font-heading text-sm text-foreground-950">相关推荐</h2>
                <p className="mt-1 font-label text-[11px] text-foreground-500">根据剧情标签与时间线关联</p>
                <div className="mt-4 flex flex-col divide-y divide-background-200">
                  {related.map((item) => (
                    <Link
                      key={item.id}
                      to={`/detail/${item.id}`}
                      className="group flex items-center gap-3 py-3 first:pt-0 last:pb-0"
                    >
                      <div className="h-14 w-20 shrink-0 overflow-hidden rounded-md border border-background-200">
                        <img
                          src={item.cover}
                          alt={`${item.title} 章节缩略图`}
                          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                        />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm text-foreground-900 transition-colors group-hover:text-accent-400">
                          {item.title}
                        </p>
                        <p className="mt-0.5 font-label text-[11px] text-foreground-500">
                          第 {item.index} 章 · {item.segment} · {item.duration} 分钟
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>

              <div className="rounded-lg border border-background-200 bg-background-200/40 p-5">
                <p className="font-label text-[11px] tracking-[0.18em] text-accent-500">观影提示</p>
                <p className="mt-2 text-xs leading-relaxed text-foreground-600">
                  播放区域为前端演示模块，暂未接入真实播放接口。站内剧情解说与影评均为原创整理，影片资料仅作示例。
                </p>
                <Link
                  to="/about#disclaimer"
                  className="mt-3 inline-flex items-center gap-1 font-label text-xs text-accent-500 hover:text-accent-400"
                >
                  阅读完整声明
                  <i className="ri-arrow-right-line text-sm leading-none" />
                </Link>
              </div>
            </aside>
          </div>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}